import React, { FC } from 'react';
import MediaPreview from 'app/components/MediaPreview';
import EditableLinkCardForm from 'app/components/linkAttachments/components/editableLinkCards/EditableLinkCardForm';
import { AttachmentConvertor } from 'utils/attachments';
import MediaLibraryModal from 'app/modules/mediaLibrary/MediaLibraryModal';
import { AttachmentCard } from 'types/Attachments';
import { Icon, IconButton } from '@kontentino/ui';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { MediaLibraryItem } from 'types/MediaLibrary';
import ImageEditor from 'app/components/ImageEditor';
import { useImageEditor } from 'app/components/imageEditor/hooks/useImageEditor';
import FileSourceInput from 'app/components/FileSourceInput';
import {
  useFileSource,
  UseFileSourcesProps,
} from 'app/components/fileSourceInput/hooks/useFileSource';
type Props = {
  page: {
    id: number;
    type: number;
  };
  attachmentCardIndex?: number;
  attachmentCard: AttachmentCard;
  onAttachmentCardChange(card: Partial<AttachmentCard>): void;
  onAttachmentCardRemove?(): void;
  isEditable?: boolean;
  isImageRemovable?: boolean;
  withDescriptionField?: boolean;
  isFullLinkEditable?: boolean;
};

const MAX_FILES = 1;

const EditableLinkCard: FC<Props> = ({
  attachmentCardIndex,
  onAttachmentCardChange,
  onAttachmentCardRemove,
  isEditable,
  isImageRemovable,
  withDescriptionField,
  attachmentCard,
  page,
  isFullLinkEditable,
}) => {
  const fileSourceProps = {
    maxFiles: MAX_FILES,
    allowedFiles: ['image'],
    page,
    onChange: ([media]) => handleMediumChange(media),
  } satisfies UseFileSourcesProps;
  const fileSource = useFileSource(fileSourceProps);
  const { imageToEdit, editImage, hideImageEditor } = useImageEditor();

  function handleMediumChange(mediaLibraryItem?: MediaLibraryItem) {
    if (!mediaLibraryItem) return;

    const { medium, is_original_link_media } =
      AttachmentConvertor.fromMediaLibraryItem(mediaLibraryItem);

    onAttachmentCardChange({ medium, is_original_link_media });
  }

  const orderIndex =
    typeof attachmentCardIndex === 'number'
      ? attachmentCardIndex + 1
      : undefined;

  return (
    <>
      <div
        key={`carousel-card-${attachmentCardIndex}`}
        className="tw-relative tw-flex tw-gap-6 tw-rounded tw-bg-grayscale-5 tw-p-4"
      >
        {onAttachmentCardRemove && (
          <IconButton
            icon={<Icon icon={faXmark} />}
            variant="ghost"
            className="tw-absolute tw-right-0 tw-top-0"
            onClick={onAttachmentCardRemove}
          />
        )}
        <div>
          {fileSource.mediaUploader.runningUploads.length > 0 ? (
            <MediaPreview
              isLoading={true}
              orderIndex={orderIndex}
              src={fileSource.mediaUploader.runningUploads[0].base64Preview}
            />
          ) : (
            <>
              {attachmentCard.medium && (
                <MediaPreview
                  orderIndex={orderIndex}
                  src={
                    attachmentCard.medium?.type === 'image'
                      ? attachmentCard.medium.sizes.lightbox.src
                      : attachmentCard.medium?.sizes.medium?.src
                  }
                  subActionHandlers={
                    isEditable
                      ? {
                          onRemoveClick: isImageRemovable
                            ? () => onAttachmentCardChange({ medium: null })
                            : undefined,
                          onReplaceClick: () => fileSource.mediaLibrary.show(),
                          onEditClick:
                            attachmentCard.medium?.type === 'image' &&
                            attachmentCard.medium.sizes.original.src
                              ? () =>
                                  editImage({
                                    // @ts-ignore
                                    src: attachmentCard.medium.sizes.original
                                      .src,
                                  })
                              : undefined,
                        }
                      : undefined
                  }
                />
              )}
              {!attachmentCard.medium && (
                <FileSourceInput
                  variant="picker"
                  disabled={!isEditable}
                  fileSourceController={fileSource}
                />
              )}
            </>
          )}
        </div>
        <div className="tw-flex-1">
          <EditableLinkCardForm
            attachmentCard={attachmentCard}
            disabled={!isEditable}
            onChange={(attachmentCard) =>
              onAttachmentCardChange(attachmentCard)
            }
            withDescriptionField={withDescriptionField}
            isFullLinkEditable={isFullLinkEditable}
          />
        </div>
      </div>
      <MediaLibraryModal
        limits={{
          selectCount: MAX_FILES,
          allowedFiles: ['image'],
        }}
        page={page}
        isShown={fileSource.mediaLibrary.isShown}
        onHide={fileSource.mediaLibrary.hide}
        onSelectSubmit={([item]) => handleMediumChange(item)}
      />
      {imageToEdit?.src && (
        <ImageEditor
          pageId={page.id}
          imageSrc={imageToEdit?.src}
          onClose={() => hideImageEditor()}
          onImageEdited={(file) => {
            fileSource.mediaUploader.upload({ files: [file] });
          }}
        />
      )}
    </>
  );
};
export default EditableLinkCard;
